import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import * as React from 'react'
import { Helmet } from 'react-helmet'
import type { StripeCustomer } from 'types/payments'

import api from '../../api'
import Spinner from '../Spinner'
import { SetupIntentForm } from './SetupIntentForm'

type Props = {
  stripeCustomer: StripeCustomer | null | undefined,
  onChange?: () => undefined | Promise<undefined>,
  title?: string,
}

export function StripeForm({
  stripeCustomer,
  onChange,
  title,
}: Props): React.Node {
  const [publishableKey, setPublishableKey] = React.useState(null)
  const [clientSecret, setClientSecret] = React.useState(null)
  const [textMessage, setTextMessage] = React.useState(
    title || 'Enter your payment details to get started.'
  )
  const [error, setError] = React.useState(false)

  const AppBase = process.env.REACT_APP_SITE_URL || ''

  React.useEffect(() => {
    async function fetchData() {
      const { key } = await api.getStripeConfig()
      const { client_secret, error } = await api.createSetupIntent()
      setPublishableKey(key)
      setClientSecret(client_secret)
      setError(Boolean(error))
    }
    fetchData()
  }, [])

  React.useEffect(() => {
    const { subscription_status } = stripeCustomer ?? {}

    switch (subscription_status) {
      case 'unpaid':
      case 'canceled':
      case 'past_due':
        return setTextMessage(
          'Your payment method was unable to be processed. Please update it to continue.'
        )
      case 'incomplete':
        return setTextMessage(
          'Please provide more information about your payment.'
        )
      default:
        return setTextMessage(
          title || 'Enter your payment details to get started.'
        )
    }
  }, [stripeCustomer, title])

  const renderContent = React.useCallback(() => {
    if (error) {
      return (
        <div className="flex justify-center bg-white py-3 text-sm text-gray-700">
          <p>Something went wrong. Please try again later.</p>
        </div>
      )
    }

    if (publishableKey && clientSecret) {
      return (
        <Elements
          options={{ clientSecret }}
          stripe={loadStripe(publishableKey)}
        >
          <SetupIntentForm
            message={textMessage}
            onChange={onChange}
            returnURL={AppBase}
          />
        </Elements>
      )
    }

    return (
      <div className="flex justify-center bg-white py-3 text-sm text-gray-700">
        <Spinner color="onLight" size={20} />
      </div>
    )
  }, [AppBase, clientSecret, error, onChange, publishableKey, textMessage])

  return (
    <>
      <Helmet>
        <title>Setup Payment/Zentake</title>
      </Helmet>
      {renderContent()}
    </>
  )
}
export default StripeForm
